import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Tag,
  Modal,
  Input,
  Space,
  Typography,
  Divider,
  Alert,
  Timeline,
  message,
} from "antd";
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  FastForwardOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  StopOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import UploadButton from "../../../components/botones/UploadButton";
import PageHeader from "../../../components/headers/PageHeader";
import { formatLimpiar } from "../../../utils/formatLimpiar";
import activitiesService from "../../../services/activitiesService";
import { useUser } from "../../../context/UserContext";
import resourceService from "../../../services/recursosService";
import { getActivityStateConfig } from "./activityStates";

// Función para obtener el comentario más reciente
const obtenerDatoMasReciente = (arrayDeObjetos) => {
  if (!arrayDeObjetos || arrayDeObjetos.length === 0) {
    return { comment: "Sin comentarios" };
  }

  const arrayOrdenado = arrayDeObjetos
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return arrayOrdenado[0];
};

const { Text } = Typography;
const { TextArea } = Input;

const ActivityManager = ({
  activity,
  consultaACTIVIDADES,
  onActivityUpdate,
  fetchData,
}) => {
  const user = useUser();
  const [activityState, setActivityState] = useState(activity);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [currentAction, setCurrentAction] = useState("");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [evidences, setEvidences] = useState([]);
  const [evidencesLoading, setEvidencesLoading] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);

  useEffect(() => {
    setActivityState(activity);
  }, [activity]);

  // Función para consultar evidencias del evento
  const consultarEvidencias = async () => {
    if (!activity?.eventId) return;

    setEvidencesLoading(true);
    try {
      const response = await resourceService.consultarRecursos(
        user?.idToken ?? "",
        activity.eventId || ""
      );

      // Extraer las evidencias de la respuesta
      const evidencias = response?.Items || [];
      setEvidences(evidencias);
    } catch (error) {
      console.error("Error al consultar evidencias:", error);
      setEvidences([]);
    } finally {
      setEvidencesLoading(false);
    }
  };

  // Consultar evidencias cuando se carga el componente
  useEffect(() => {
    if (activity?.eventId) {
      consultarEvidencias();
    }
  }, []);

  // Función para eliminar una evidencia
  const eliminarEvidencia = async (fileName, id) => {
    setDeletingFile(fileName);
    try {
      await resourceService.eliminaRecurso(user?.idToken ?? "", id || "");
      setEvidences((prevEvidences) =>
        prevEvidences.filter((ev) => ev.name !== fileName)
      );
      message.success(`Archivo "${fileName}" eliminado correctamente`);
    } catch (error) {
      console.error("Error al eliminar evidencia:", error);
      message.error("Error al eliminar el archivo");
    } finally {
      setDeletingFile(null);
    }
  };

  // Función para ver el archivo
  const verArchivo = (url) => {
    if (url) {
      window.open(url, "_blank");
    } else {
      message.warning("URL del archivo no disponible");
    }
  };

  // Función para obtener el color del botón según la acción
  const getButtonColor = (action) => {
    const colors = {
      start: "#1890ff",
      complete: "#52c41a",
      skip: "#d9d9d9",
      terminate: "#52c41a",
    };
    return colors[action];
  };

  // Función para actualizar el estado de la actividad
  const updateActivityState = (newState, commentText = "") => {
    if (!activityState) return;

    const updatedStateHistory = {
      ...activityState.stateHistory,
      [newState]: {
        date: new Date().toISOString(),
        userId: "current-user-id",
      },
    };

    const updatedComments = commentText
      ? [
          ...(activityState.comments || []),
          {
            id: Date.now().toString(),
            text: commentText,
            date: new Date().toISOString(),
            userId: "current-user-id",
          },
        ]
      : activityState.comments;

    setActivityState({
      ...activityState,
      state: newState,
      stateHistory: updatedStateHistory,
      comments: updatedComments,
      updatedAt: new Date().toISOString(),
    });
  };

  // Función para manejar acciones con comentarios
  const handleActionWithComment = (action) => {
    if (!activityState) return;

    if (activityState.isCommentMandatory && action !== "start") {
      setCurrentAction(action);
      setShowCommentModal(true);
    } else {
      executeAction(action);
    }
  };

  // Función para ejecutar la acción
  const executeAction = async (action, commentText = "") => {
    setLoading(true);

    // Simular delay de API

    let newState = "";
    let falseAlarm = false;

    switch (action) {
      case "start":
        newState = "Started";

        break;
      case "complete":
        // newState = "Completed";
        newState = activityState?.protocol_activity_subactivity?.includes(
          "cerrar_evento"
        )
          ? "Terminated"
          : "Completed";
        break;
      case "terminate":
        newState = "Completed";
        break;
      case "false-alarm-yes":
        falseAlarm = true;
        newState = "Completed";
        break;
      case "false-alarm-no":
        newState = activityState?.protocol_activity_subactivity?.includes(
          "cerrar_evento"
        )
          ? "Terminated"
          : "Completed";
        break;
      case "skip":
        newState = "Skipped";
        break;
      default:
        setLoading(false);
        return;
    }

    let registro = {
      eventId: activity.eventId,
      protocol_activity_subactivity: activity.protocol_activity_subactivity,
      state: newState,
      order: activity.order,
      comment: commentText,
      falseAlarm: falseAlarm,
    };

    try {
      // Llamar al servicio para ejecutar la acción sobre la actividad
      await activitiesService.funcionActividad(registro, user?.idToken ?? "");

      updateActivityState(newState, commentText);

      // Solo cerrar el modal y limpiar el comentario si la API fue exitosa
      setShowCommentModal(false);
      setComment("");
      setCurrentAction("");

      // Notificar al componente padre sobre la actualización
      if (onActivityUpdate) {
        onActivityUpdate(activity, newState);
      }

      // Si es falsa alarma, actualizar la lista de eventos
      if (falseAlarm && fetchData) {
        fetchData();
      }

      // Si es cerrar evento, actualizar tanto la lista de eventos como las actividades
      const isCloseEvent =
        activityState?.protocol_activity_subactivity?.includes("cerrar_evento");
      if (
        isCloseEvent &&
        (action === "terminate" || action === "complete") &&
        fetchData
      ) {
        fetchData();
        // También actualizar las actividades si se proporciona la función
        if (consultaACTIVIDADES) {
          consultaACTIVIDADES();
        }
      }
    } catch (error) {
      console.error("Error al ejecutar la acción de la actividad:", error);
      message.error(
        "No se logró realizar la acción. Puedes intentar nuevamente."
      );
      // NO cerrar el modal ni limpiar el comentario en caso de error
      // El modal permanecerá abierto con el comentario del usuario
    } finally {
      // Finalizar la animación de carga
      setLoading(false);
      // NO limpiar el comentario aquí, solo en caso de éxito
    }
  };

  // Función para confirmar acción con comentario
  const confirmAction = () => {
    if (!activityState) {
      setShowCommentModal(false);
      return;
    }

    if (activityState.isCommentMandatory && !comment.trim()) {
      Modal.error({
        title: "Error",
        content: "El comentario es obligatorio para esta actividad",
      });
      return;
    }

    executeAction(currentAction, comment);
  };

  // Función para cancelar acción
  const cancelAction = () => {
    setShowCommentModal(false);
    setComment("");
    setCurrentAction("");
  };

  // Función para iniciar edición de comentario
  const startEditComment = async (commentId, currentText) => {
    setEditingCommentId(commentId);
    setEditingCommentText(currentText);
  };

  // Función para guardar comentario editado
  const saveEditedComment = async (commentId) => {
    if (!editingCommentText.trim()) {
      Modal.error({
        title: "Error",
        content: "El comentario no puede estar vacío",
      });
      return;
    }

    setLoading(true); // Activar estado de carga

    let registro = {
      comment: editingCommentText.trim(),
    };

    try {
      // Llamar al servicio para ejecutar la acción sobre la actividad
      const response = await activitiesService.editarComentario(
        registro,
        user?.idToken ?? "",
        activity.eventId,
        activity.protocol_activity_subactivity
      );

      // Mostrar mensaje de éxito
      message.success("Comentario editado correctamente");

      // Actualizar el estado local del comentario
      const updatedComments = activityState.comments.map((comment) =>
        comment.id === commentId
          ? {
              ...comment,
              text: editingCommentText.trim(),
              editedAt: new Date().toISOString(),
            }
          : comment
      );

      setActivityState({
        ...activityState,
        comments: updatedComments,
        updatedAt: new Date().toISOString(),
      });

      setEditingCommentId(null);
      setEditingCommentText("");
    } catch (error) {
      console.error("Error al ejecutar la acción de la actividad:", error);
      message.error(
        "No se logró realizar la acción. Puedes intentar nuevamente."
      );
      // NO cerrar el modal ni limpiar el comentario en caso de error
      // El modal permanecerá abierto con el comentario del usuario
    } finally {
      // Finalizar la animación de carga
      setLoading(false);
    }
  };

  // Función para cancelar edición de comentario
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };
  // Función para renderizar botones según el estado y tipo de actividad
  const renderButtons = () => {
    if (!activityState) return null;

    const isFalseAlarm =
      activityState.protocol_activity_subactivity?.includes("falsa-alarma");
    const isCloseEvent =
      activityState.protocol_activity_subactivity?.includes("cerrar_evento");

    switch (activityState.state) {
      case "Created":
        return (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            size="large"
            loading={loading}
            onClick={() => handleActionWithComment("start")}
            style={{ backgroundColor: getButtonColor("start") }}
          >
            Iniciar
          </Button>
        );

      case "Started":
        if (isFalseAlarm) {
          return (
            <div
              style={{
                maxWidth: 460,
                width: "100%",
                margin: "0 auto",
                padding: "20px 24px",
                background: "#fff9f8",
                border: "1px solid #ffccc7",
                borderRadius: 12,
                textAlign: "center",
              }}
            >
              <ExclamationCircleOutlined
                style={{ fontSize: 28, color: "#ff4d4f", marginBottom: 8 }}
              />
              <Text strong style={{ display: "block", fontSize: 15 }}>
                ¿La alerta era real?
              </Text>
              <Text
                type="secondary"
                style={{ display: "block", fontSize: 13, marginBottom: 20 }}
              >
                Confirma si el evento fue una falsa alarma antes de continuar
                con el protocolo.
              </Text>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  size="large"
                  loading={loading}
                  onClick={() => handleActionWithComment("false-alarm-no")}
                >
                  No, continuar protocolo
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  size="large"
                  loading={loading}
                  onClick={() => handleActionWithComment("false-alarm-yes")}
                >
                  Sí, es falsa alarma
                </Button>
              </div>
            </div>
          );
        }

        return (
          <Space wrap>
            <Button
              type="primary"
              icon={isCloseEvent ? <StopOutlined /> : <CheckCircleOutlined />}
              size="large"
              loading={loading}
              onClick={() =>
                handleActionWithComment(isCloseEvent ? "terminate" : "complete")
              }
              style={{
                backgroundColor: getButtonColor(
                  isCloseEvent ? "terminate" : "complete"
                ),
              }}
            >
              {isCloseEvent ? "Terminar evento" : "Completar"}
            </Button>
            {!activityState.isMandatory && (
              <Button
                icon={<FastForwardOutlined />}
                size="large"
                loading={loading}
                onClick={() => handleActionWithComment("skip")}
              >
                Saltar
              </Button>
            )}
          </Space>
        );

      default:
        return null;
    }
  };

  if (!activity || !activityState) {
    return (
      <div style={{ padding: "24px" }}>
        <Alert
          message="Sin actividad"
          description="No hay actividad para mostrar"
          type="info"
          showIcon
        />
      </div>
    );
  }

  const stateConfig = getActivityStateConfig(activityState.state);

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      <Card
        title={
          <PageHeader
            title={
              "Paso " +
              activity.order +
              " " +
              formatLimpiar(
                activity.protocol_activity_subactivity.split("_")[1] || ""
              )
            }
          ></PageHeader>
        }
        extra={<Tag color={stateConfig.color}>{stateConfig.label}</Tag>}
        style={{
          backgroundColor: stateConfig.bg,
          border: `2px solid ${stateConfig.border}`,
        }}
        hoverable
      >
        <Text
          type="secondary"
          style={{ display: "block", marginBottom: "12px" }}
        >
          Descripción:{" "}
          {activityState?.description ||
            "Genera la actividad usando los botones"}
        </Text>

        {/* Mostrar último comentario */}
        {activityState.comments && activityState.comments.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              backgroundColor: "#f5f5f5",
              borderRadius: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                <Text strong style={{ display: "block", marginBottom: "4px" }}>
                  <MessageOutlined /> Último Comentario:
                </Text>
                <Text style={{ display: "block", marginBottom: "4px" }}>
                  {obtenerDatoMasReciente(activityState.comments).comment}
                </Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {obtenerDatoMasReciente(activityState.comments).editedAt ? (
                    <>
                      Editado:{" "}
                      {new Date(
                        obtenerDatoMasReciente(activityState.comments).editedAt
                      ).toLocaleString()}
                    </>
                  ) : (
                    obtenerDatoMasReciente(activityState.comments).date && (
                      <>
                        {new Date(
                          obtenerDatoMasReciente(activityState.comments).date
                        ).toLocaleString()}
                      </>
                    )
                  )}
                </Text>
              </div>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  startEditComment(
                    obtenerDatoMasReciente(activityState.comments).id,
                    obtenerDatoMasReciente(activityState.comments).text
                  )
                }
                title="Editar comentario"
              >
                Editar
              </Button>
            </div>
          </div>
        )}

        {activityState.comments && activityState.comments.length > 0 && (
          <>
            <Divider orientation="left">
              <Text strong>
                <MessageOutlined /> Comentarios
              </Text>
            </Divider>
            <Timeline>
              {activityState.comments.map((comment, idx) => (
                <Timeline.Item key={comment.id || idx}>
                  <div style={{ marginBottom: "8px" }}>
                    {editingCommentId === comment.id ? (
                      <div>
                        <TextArea
                          value={editingCommentText}
                          onChange={(e) =>
                            setEditingCommentText(e.target.value)
                          }
                          rows={3}
                          style={{ marginBottom: "8px" }}
                          showCount
                        />
                        <Space>
                          <Button
                            type="primary"
                            size="small"
                            icon={<SaveOutlined />}
                            onClick={() => saveEditedComment(comment.id)}
                            loading={loading}
                          >
                            Guardar
                          </Button>
                          <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={cancelEditComment}
                          >
                            Cancelar
                          </Button>
                        </Space>
                      </div>
                    ) : (
                      <div>
                        <Text>{comment.text}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {comment.editedAt ? (
                            <>
                              Editado:{" "}
                              {new Date(comment.editedAt).toLocaleString()}
                            </>
                          ) : (
                            comment.date && (
                              <>{new Date(comment.date).toLocaleString()}</>
                            )
                          )}
                        </Text>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </>
        )}

        <Divider />

        <div style={{ textAlign: "center" }}>{renderButtons()}</div>

        {/* Contador de evidencias */}
        <div
          style={{
            marginTop: "10px",
            marginBottom: "16px",
            padding: "8px 12px",
            backgroundColor: "#f0f8ff",
            borderRadius: "6px",
            border: "1px solid #d6e4ff",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Text strong style={{ color: "#1890ff" }}>
                📁 Evidencias del evento
              </Text>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={consultarEvidencias}
                loading={evidencesLoading}
                title="Refrescar evidencias"
              />
            </div>
            {evidencesLoading ? (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Cargando...
              </Text>
            ) : (
              <Text strong style={{ color: "#52c41a" }}>
                {evidences.length} archivo{evidences.length !== 1 ? "s" : ""}
              </Text>
            )}
          </div>
          <Space></Space>
          {/* Lista de evidencias */}
          {evidences.length > 0 && (
            <div style={{ marginTop: "8px" }}>
              <Text
                type="secondary"
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                Archivos:
              </Text>
              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                {evidences.map((evidencia, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "4px 8px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                      marginBottom: "4px",
                      border: "1px solid #e8e8e8",
                    }}
                  >
                    <Text
                      style={{ fontSize: "12px", flex: 1, marginRight: "8px" }}
                    >
                      {evidencia.name || "Archivo sin nombre"}
                    </Text>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => verArchivo(evidencia.url)}
                        title="Ver archivo"
                        style={{ padding: "2px 4px" }}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          eliminarEvidencia(evidencia.name, evidencia.id)
                        }
                        loading={deletingFile === evidencia.name}
                        danger
                        title="Eliminar archivo"
                        style={{ padding: "2px 4px" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {activityState.state !== "Created" && (
          <UploadButton
            eventId={activityState.eventId}
            setKey={""}
            protocol={activityState.protocol_activity_subactivity.split("_")[0]}
            activity={activityState.protocol_activity_subactivity}
            extras={false}
            onUploadComplete={consultarEvidencias}
          />
        )}
      </Card>

      {/* Modal para comentarios */}
      <Modal
        title={
          <Space>
            <MessageOutlined />
            {currentAction === "false-alarm-yes"
              ? "Justifica la falsa alarma"
              : activityState?.isCommentMandatory
              ? "Comentario obligatorio"
              : "Agregar comentario"}
          </Space>
        }
        open={showCommentModal}
        onOk={confirmAction}
        onCancel={cancelAction}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={loading}
        width={800}
      >
        {activityState?.isCommentMandatory && (
          <Alert
            message="Este comentario es obligatorio"
            type="warning"
            showIcon
            style={{ marginTop: "12px" }}
          />
        )}

        <TextArea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Escribe tu comentario aquí..."
          rows={10}
          showCount
        />
      </Modal>
    </div>
  );
};

export default ActivityManager;
